import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState } from 'react';
import { BarChart3, GitBranch, Filter, Activity, CalendarClock, Headphones, LineChart, Award } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { cn } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { tierOf, tierLabelOf } from '@/shared/permissions/identity';
import { PipelinePanel } from '../components/PipelinePanel';
import { FunnelPanel } from '../components/FunnelPanel';
import { ActivityPanel } from '../components/ActivityPanel';
import { AttendancePanel } from '../components/AttendancePanel';
import { CallCenterPanel } from '../components/CallCenterPanel';
import { ForecastPanel } from '../components/ForecastPanel';
import { Section3AnalyticsPanel } from '@/features/section5/Section3AnalyticsPanel';
import { CommercialPerformancePanel } from '../components/CommercialPerformancePanel';

type TabKey = 'pipeline' | 'funnel' | 'forecast' | 'activity' | 'attendance' | 'call-center' | 'commercial';

const ALL_TABS: { key: TabKey; label: string; icon: LucideIcon }[] = [
  { key: 'pipeline', label: 'Pipeline', icon: GitBranch },
  { key: 'funnel', label: 'Funnel', icon: Filter },
  { key: 'forecast', label: 'Forecast', icon: LineChart },
  { key: 'activity', label: 'Activity', icon: Activity },
  { key: 'attendance', label: 'Attendance', icon: CalendarClock },
  { key: 'call-center', label: 'Call Center', icon: Headphones },
  { key: 'commercial', label: 'Commercial performance', icon: Award },
];

export function AnalyticsPage() {
  const user = useUser();
  // The /api/v2/analytics/* endpoints are `requireAdmin`. Gate the whole surface
  // to the Administrator and Executive tiers using identity.ts — never a raw
  // role string (Req 13.1, 13.2).
  const tier = tierOf(user?.role);
  const isPrivileged = tier === 'ADMIN' || tier === 'SUPER_ADMIN';
  const showSales = isPrivileged && (tier === 'SUPER_ADMIN' || user?.department === 'sales');

  const tabs = showSales 
    ? ALL_TABS 
    : ALL_TABS.filter(t => ['activity', 'attendance'].includes(t.key));

  const [tab, setTab] = useState<TabKey>(tabs[0]?.key || 'activity');

  if (!isPrivileged) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <EmptyState
          title="Analytics unavailable"
          description="Analytics dashboards are available to administrators only."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto pb-16">
      <PageHeader
        icon={BarChart3}
        eyebrow="Insights"
        title="Analytics"
        description={<>{tierLabelOf(user?.role)} view · Read-only dashboards over the live pipeline.</>}
      />

      {/* Tabs */}
      <div role="tablist" aria-label="Analytics dashboards" className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              role="tab"
              aria-selected={active}
              className={cn(
                'flex items-center gap-2 border-b-2 px-3 py-2 text-body-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-variant hover:text-on-surface',
              )}
            >
              <t.icon className="h-4 w-4" aria-hidden />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panels — mounted only when active so each fetches on demand. */}
      <div className="flex-1">
        {tab === 'pipeline' && <PipelinePanel enabled={isPrivileged} />}
        {tab === 'funnel' && <FunnelPanel enabled={isPrivileged} />}
        {tab === 'forecast' && <ForecastPanel enabled={isPrivileged} />}
        {tab === 'activity' && <ActivityPanel enabled={isPrivileged} />}
        {tab === 'attendance' && <AttendancePanel enabled={isPrivileged} />}
        {tab === 'call-center' && <CallCenterPanel enabled={isPrivileged} />}
        {tab === 'commercial' && <CommercialPerformancePanel enabled={isPrivileged} />}
      </div>

      <Section3AnalyticsPanel enabled={isPrivileged} />
    </div>
  );
}
